/**
 * Tooltip Component - Design System
 *
 * Componente de tooltip reutilizable que sigue el sistema de diseño de Owly CRM.
 * Usa Portal para renderizar fuera del DOM y evitar problemas de overflow.
 *
 * @example
 * ```tsx
 * <Tooltip content="Hacer una llamada">
 *   <Button>Llamar</Button>
 * </Tooltip>
 * ```
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

export interface TooltipProps {
  /** Contenido del tooltip */
  content: string;
  /** Posición del tooltip */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Delay antes de mostrar (ms) */
  delay?: number;
  /** Elemento hijo que dispara el tooltip */
  children: React.ReactElement;
  /** Deshabilitado */
  disabled?: boolean;
  /** Clase adicional para el contenedor */
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  position = 'top',
  delay = 200,
  children,
  disabled = false,
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 8; // Gap between trigger and tooltip

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = rect.top - gap;
        left = rect.left + rect.width / 2;
        break;
      case 'bottom':
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2;
        left = rect.left - gap;
        break;
      case 'right':
        top = rect.top + rect.height / 2;
        left = rect.right + gap;
        break;
    }

    setTooltipPosition({ top, left });
  }, [position]);

  const handleMouseEnter = () => {
    if (disabled) return;
    calculatePosition();
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Estilos de transformación según posición
  const getTransformStyle = () => {
    switch (position) {
      case 'top':
        return 'translate(-50%, -100%)';
      case 'bottom':
        return 'translate(-50%, 0)';
      case 'left':
        return 'translate(-100%, -50%)';
      case 'right':
        return 'translate(0, -50%)';
    }
  };

  // Arrow position styles
  const getArrowStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderWidth: 4,
    };

    switch (position) {
      case 'top':
        return {
          ...base,
          bottom: -8,
          left: '50%',
          transform: 'translateX(-50%)',
          borderColor: 'rgb(17 24 39) transparent transparent transparent',
        };
      case 'bottom':
        return {
          ...base,
          top: -8,
          left: '50%',
          transform: 'translateX(-50%)',
          borderColor: 'transparent transparent rgb(17 24 39) transparent',
        };
      case 'left':
        return {
          ...base,
          right: -8,
          top: '50%',
          transform: 'translateY(-50%)',
          borderColor: 'transparent transparent transparent rgb(17 24 39)',
        };
      case 'right':
        return {
          ...base,
          left: -8,
          top: '50%',
          transform: 'translateY(-50%)',
          borderColor: 'transparent rgb(17 24 39) transparent transparent',
        };
    }
  };

  return (
    <div
      ref={triggerRef}
      className={className || 'inline-block'}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {/* Tooltip - renderizado via Portal */}
      {isVisible && !disabled && createPortal(
        <div
          style={{
            position: 'fixed',
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            transform: getTransformStyle(),
            zIndex: 999999,
            pointerEvents: 'none',
          }}
          role="tooltip"
        >
          {/* Tooltip content */}
          <div className="px-3 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-xs font-medium rounded-lg shadow-lg whitespace-nowrap">
            {content}
          </div>

          {/* Arrow */}
          <div style={getArrowStyle()} />
        </div>,
        document.body
      )}
    </div>
  );
};

/**
 * Tooltip simple sin wrapper div
 * Útil cuando no quieres un div adicional en el DOM
 */
export const TooltipInline: React.FC<TooltipProps> = (props) => {
  return <Tooltip {...props} />;
};

export default Tooltip;
